import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  model,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import {
  createAnchorName,
  createPopoverId,
} from '../shared/anchor-positioning';
import { TwPopoverPlacement } from '../shared/types';

import {
  TW_POPOVER_CONTEXT,
  TwPopoverContext,
  TwPopoverTriggerMode,
} from './tw-popover-context';

/**
 * `tw-popover` — parent orchestrator for the popover family
 * (TWS-122 Wave 2).
 *
 * Slot composition:
 *
 * ```html
 * <tw-popover [(open)]="state.open" placement="top" triggerOn="click">
 *   <button twBtnStyle variant="ghost" size="md" twPopoverTrigger>Details</button>
 *   <tw-popover-content>
 *     <p>Helpful context, maybe with a form…</p>
 *     <tw-popover-arrow />
 *   </tw-popover-content>
 * </tw-popover>
 * ```
 *
 * The trigger is an attribute directive on a real `<button>` —
 * `popovertarget` is a button-specific HTML attribute and the
 * native invoker semantics (click / Enter / Space / light-dismiss /
 * Escape) only fire when the attribute is on a real `<button>`
 * (or `<input>`).
 *
 * Unlike `<tw-dropdown>`, popovers host arbitrary dialog content
 * so the content slot has `role="dialog"`. For purely
 * informational hover/focus hints use `[twTooltip]` instead —
 * the two primitives occupy intentionally different semantic
 * roles.
 *
 * ### Host styling
 *
 * `display: contents` — same rationale as `<tw-dropdown>`: the
 * parent is a logical container that shouldn't disrupt the
 * consumer's flex/grid layout.
 */
@Component({
  selector: 'tw-popover',
  standalone: true,
  template: '<ng-content></ng-content>',
  host: { 'class': 'tw-popover', 'style': 'display: contents;' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    // Mirror Wave 1's dropdown pattern: DI token + `useFactory`
    // with the component itself as a dep keeps the context methods
    // off the public component surface.
    {
      provide: TW_POPOVER_CONTEXT,
      useFactory: (component: TwPopoverComponent) => component.context,
      deps: [TwPopoverComponent],
    },
  ],
})
export class TwPopoverComponent {
  // ---------------------------------------------------------------
  // Signal inputs (public)
  // ---------------------------------------------------------------

  /**
   * Two-way open state. Consumers bind `[(open)]="state.open"` to
   * keep the parent signal in lockstep with the native `toggle`
   * event. Content also calls `setOpen(false)` on native
   * light-dismiss so this stays accurate.
   */
  readonly open = model<boolean>(false);

  /**
   * Preferred anchor side. The content's SCSS reads this via
   * `data-placement` and selects the matching anchor mixin;
   * `position-try-fallbacks` handles auto-flip when the preferred
   * side would overflow.
   */
  readonly placement = model<TwPopoverPlacement>('top');

  /**
   * `click` (default) uses the native `popovertarget` attribute
   * on the trigger — zero JS on the happy path. `hover` disables
   * `popovertarget` and wires mouse/focus listeners. Click is the
   * default because popover content is frequently interactive and
   * accidental-hover-open is a bad experience for form content.
   */
  readonly triggerOn = model<TwPopoverTriggerMode>('click');

  // ---------------------------------------------------------------
  // Outputs
  // ---------------------------------------------------------------

  /** Fires once when the popover opens. */
  readonly opened = output<void>();
  /** Fires once when the popover closes. */
  readonly closed = output<void>();

  // ---------------------------------------------------------------
  // Internals — stable id pair + trigger reference.
  // ---------------------------------------------------------------

  private readonly popoverId = createPopoverId();
  private readonly anchorName = createAnchorName();

  /**
   * Trigger's host element, registered on init so the content
   * slot can position-anchor against it. Nullable because
   * registration happens inside the trigger's `ngOnInit` which
   * runs after the parent's constructor.
   */
  private readonly triggerEl = signal<HTMLElement | null>(null);

  /**
   * Bridge channel for the trigger directive to receive content-
   * slot hover transitions. The trigger registers a single
   * handler in `ngOnInit`; the content slot's `mouseenter` /
   * `mouseleave` listeners call `context.notifyContentHovered(...)`
   * which forwards here. A `null` slot means no hover-mode trigger
   * is mounted (e.g. click mode) and the call is a no-op.
   */
  private contentHoverHandler: ((hovered: boolean) => void) | null = null;

  constructor() {
    // Mirror Wave 1: emit `opened` / `closed` exactly once per
    // transition. Initial `false → false` is a no-op.
    let previous = this.open();
    effect(() => {
      const next = this.open();
      if (next === previous) return;
      previous = next;
      if (next) this.opened.emit();
      else this.closed.emit();
    });
  }

  // ---------------------------------------------------------------
  // Context the DI token hands to children.
  // ---------------------------------------------------------------

  readonly context: TwPopoverContext = {
    popoverId: this.popoverId,
    anchorName: this.anchorName,
    open: computed(() => this.open()),
    placement: computed(() => this.placement()),
    triggerOn: computed(() => this.triggerOn()),
    triggerElement: computed(() => this.triggerEl()),

    setOpen: (value: boolean) => {
      this.open.set(value);
    },

    registerTriggerElement: (el: HTMLElement) => {
      this.triggerEl.set(el);
    },

    registerContentHoverHandler: (
      handler: ((hovered: boolean) => void) | null,
    ) => {
      this.contentHoverHandler = handler;
    },

    notifyContentHovered: (hovered: boolean) => {
      this.contentHoverHandler?.(hovered);
    },
  };
}
